import { useState } from 'react';
import * as LoanDB from '@/lib/personalLoansDb';
import { formatTRY } from '@/lib/currencyFormat';
import { supabase } from '@/lib/supabase';

interface RepaymentModalProps {
  loanId: string;
  remainingAmount: number;
  borrowerName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RepaymentModal({ loanId, remainingAmount, borrowerName, onClose, onSuccess }: RepaymentModalProps) {
  const [form, setForm] = useState({
    amount: '',
    movement_date: new Date().toLocaleDateString('en-CA'),
    payment_method: 'Nakit',
    description: '',
    receipt_no: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      setError('Geçerli bir ödeme tutarı giriniz.');
      return;
    }
    if (amount > remainingAmount) {
      setError(`Geri ödeme tutarı kalan borçtan büyük olamaz. Kalan: ${formatTRY(remainingAmount)}`);
      return;
    }

    setSaving(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;

      const res = await LoanDB.recordRepayment({
        loan_id: loanId,
        amount,
        movement_date: form.movement_date,
        payment_method: form.payment_method,
        description: form.description || null,
        receipt_no: form.receipt_no || null,
        created_by: userId || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setSuccess('Geri ödeme başarıyla kaydedildi!');
        setTimeout(() => onSuccess(), 1000);
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl shadow-lg w-full max-w-lg">
        <div className="border-b border-background-200/70 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-lg font-semibold text-foreground-900">Geri Ödeme Al</h3>
            <p className="text-xs text-foreground-400 mt-0.5">{borrowerName} · Kalan: {formatTRY(remainingAmount)}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex items-center gap-2">
              <i className="ri-check-line"></i>
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ödeme Tutarı (₺) *</label>
            <input
              type="number"
              value={form.amount}
              onChange={e => handleChange('amount', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="0.00"
              min="0"
              max={remainingAmount}
              step="0.01"
            />
            <p className="text-xs text-foreground-400 mt-1">Kalan borç: {formatTRY(remainingAmount)}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ödeme Tarihi</label>
              <input
                type="date"
                value={form.movement_date}
                onChange={e => handleChange('movement_date', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ödeme Yöntemi</label>
              <select
                value={form.payment_method}
                onChange={e => handleChange('payment_method', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
              >
                <option value="Nakit">Nakit</option>
                <option value="Banka Havalesi">Banka Havalesi</option>
                <option value="Elden">Elden</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Makbuz No</label>
            <input
              type="text"
              value={form.receipt_no}
              onChange={e => handleChange('receipt_no', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="İsteğe bağlı"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Açıklama</label>
            <textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
              rows={2}
              placeholder="Ödeme ile ilgili not"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                saving ? 'bg-emerald-300 text-background-50 cursor-not-allowed' : 'bg-emerald-500 text-background-50 hover:bg-emerald-600'
              }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-background-50/30 border-t-background-50 animate-spin"></div>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Ödeme Al
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}